import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, X, Music2, ArrowRight, Users } from "lucide-react";
import { CoverImage } from "./ReleaseCard";
import api from "../utils/api";

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function SearchOverlay({ onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [artists, setArtists] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debouncedQuery = useDebounce(query, 280);

  useEffect(() => {
    inputRef.current?.focus();
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Live search – artists + releases in parallel
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([]); setTotal(0); setArtists([]);
      return;
    }
    setLoading(true);
    const q = encodeURIComponent(debouncedQuery);
    Promise.all([
      api.get(`/releases/?search=${q}&limit=5`),
      api.get(`/artists/?search=${q}&limit=3`),
    ])
      .then(([relRes, artRes]) => {
        setResults(relRes.data.items || []);
        setTotal(relRes.data.total || 0);
        const artItems = artRes.data.items || artRes.data || [];
        setArtists(Array.isArray(artItems) ? artItems : []);
      })
      .catch(() => { setResults([]); setTotal(0); setArtists([]); })
      .finally(() => setLoading(false));
  }, [debouncedQuery]);

  const goToSearch = useCallback(() => {
    if (!query.trim()) return;
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    onClose();
  }, [query, navigate, onClose]);

  const handleResultClick = (release) => {
    onClose();
    window.dispatchEvent(new CustomEvent("openRelease", { detail: release }));
  };

  const hasResults = artists.length > 0 || results.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center pt-[8vh] px-4 sm:px-6 overflow-y-auto pb-6"
      style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(16px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="search-overlay"
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        data-testid="search-overlay-close"
      >
        <X size={16} />
      </button>

      <div
        className="w-full max-w-2xl"
        style={{ animation: "searchSlide 0.2s cubic-bezier(0.34,1.56,0.64,1)" }}
      >
        <div className="relative">
          <Search
            size={20}
            className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${query ? "text-rose-400" : "text-zinc-500"}`}
          />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") goToSearch(); }}
            placeholder="Interpret, Titel, Artist suchen…"
            data-testid="search-overlay-input"
            className="w-full bg-zinc-900 border border-white/10 focus:border-rose-500/60 rounded-2xl py-4 pl-12 pr-4 text-white text-lg font-medium placeholder-zinc-400 outline-none transition-all shadow-2xl"
          />
          {loading && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {hasResults && (
          <div className="mt-3 bg-zinc-900 border border-white/10 rounded-2xl overflow-y-auto max-h-[60vh] shadow-2xl">

            {/* Artists */}
            {artists.length > 0 && (
              <div className="border-b border-white/5">
                <div className="flex items-center gap-1.5 px-4 pt-3 pb-1.5">
                  <Users size={11} className="text-zinc-600" />
                  <span className="text-zinc-600 text-xs font-bold uppercase tracking-widest">Künstler</span>
                </div>
                <div className="divide-y divide-white/[0.04]">
                  {artists.map((artist) => {
                    const initials = (artist.name || "?").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
                    return (
                      <Link
                        key={artist.id || artist.slug}
                        to={`/artist/${artist.slug}`}
                        onClick={onClose}
                        data-testid={`search-overlay-artist-${artist.slug}`}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors group"
                      >
                        <div className="relative w-8 h-8 flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-rose-900/30 border border-rose-800/30 flex items-center justify-center overflow-hidden">
                            {artist.bild_url
                              ? <img src={artist.bild_url} alt="" className="w-full h-full rounded-full object-cover" />
                              : <span className="text-rose-400 font-black text-xs leading-none">{initials}</span>
                            }
                          </div>
                          {artist.linked_user_ids?.length > 0 && (
                            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                              style={{
                                background: "linear-gradient(145deg, #60a5fa 0%, #3b82f6 45%, #1d4ed8 100%)",
                                boxShadow: "0 1px 0 rgba(255,255,255,0.3) inset, 0 2px 5px rgba(59,130,246,0.5), 0 0 0 1.5px rgba(24,24,27,0.9)",
                              }}>
                              <svg viewBox="0 0 12 12" fill="none" style={{ width: "6px", height: "6px" }}>
                                <path d="M2 6.5L4.5 9L10 3.5" stroke="white" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-sm truncate group-hover:text-rose-300 transition-colors">
                            {artist.name}
                          </p>
                          {artist.release_count > 0 && (
                            <p className="text-zinc-600 text-xs">{artist.release_count} Releases</p>
                          )}
                        </div>
                        <ArrowRight size={12} className="text-zinc-700 group-hover:text-zinc-400 transition-colors flex-shrink-0" />
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Releases */}
            {results.length > 0 && (
              <>
                {artists.length > 0 && (
                  <div className="flex items-center gap-1.5 px-4 pt-3 pb-1.5">
                    <Music2 size={11} className="text-zinc-600" />
                    <span className="text-zinc-600 text-xs font-bold uppercase tracking-widest">Releases</span>
                  </div>
                )}
                <div className="divide-y divide-white/5">
                  {results.map((release) => (
                    <button
                      key={release.id}
                      onClick={() => handleResultClick(release)}
                      data-testid={`search-result-${release.id}`}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left group"
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-800">
                        <CoverImage
                          cover_url={release.cover_url}
                          interpret={release.interpret}
                          titel={release.titel}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-sm truncate group-hover:text-rose-300 transition-colors">
                          {release.interpret}
                        </p>
                        <p className="text-zinc-500 text-xs truncate">{release.titel}</p>
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-2">
                        {release.typ && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400">{release.typ}</span>
                        )}
                        {release.erscheinungsdatum && (
                          <span className="text-zinc-600 text-xs">{release.erscheinungsdatum?.slice(0, 4)}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}

            {total > 5 && (
              <button
                onClick={goToSearch}
                data-testid="search-overlay-show-all"
                className="w-full flex items-center justify-between px-4 py-3 bg-zinc-950 hover:bg-zinc-900 text-zinc-400 hover:text-rose-400 text-sm font-semibold transition-colors border-t border-white/5"
              >
                <span>Alle {total} Releases für „{query}" anzeigen</span>
                <ArrowRight size={14} />
              </button>
            )}
          </div>
        )}

        {debouncedQuery.trim() && !loading && !hasResults && (
          <div className="mt-3 bg-zinc-900 border border-white/10 rounded-2xl px-4 py-8 text-center shadow-2xl">
            <Music2 size={28} className="mx-auto mb-2 text-zinc-700" />
            <p className="text-zinc-500 text-sm">Keine Ergebnisse für „{debouncedQuery}"</p>
          </div>
        )}

        {!query && (
          <div className="mt-6 text-center">
            <p className="text-zinc-600 text-sm">Drücke <kbd className="px-1.5 py-0.5 bg-zinc-800 rounded text-xs text-zinc-400">Esc</kbd> zum Schließen</p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes searchSlide {
          from { opacity: 0; transform: translateY(-16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
